import React, { useMemo } from 'react'
import { Typography } from '@surecompanies/core_components'
import type { ColumnsType } from 'antd/es/table'
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  ClockCircleOutlined,
  MinusOutlined,
} from '@ant-design/icons'
import {
  BotType,
  ProcessTimeBucket,
  type CarrierSuccessRateDto,
} from '../../../../../api/dto/get-payment-bot-dashboard.dto'
import {
  GREEN,
  GOLD,
  RED,
  PROCESS_TIME_BUCKET_FILTERS,
} from '../../../shared/constants'
import {
  formatDuration,
  processTimeColor,
  rateColor,
} from '../../../shared/utils'
import { BotTypeTag } from '../BotTypeTag'
import styles from '../PaymentBotSummary.module.scss'

const { Text } = Typography

function deltaArrow(n: number): React.ReactNode {
  if (n > 0) return <ArrowUpOutlined />
  if (n < 0) return <ArrowDownOutlined />
  return <MinusOutlined />
}

// vs Prev Month shows the paid-case delta only — the business-throughput lens
// (how many more/fewer benefits actually got paid than last month). The pp delta
// was dropped per request: it cluttered the cell, and within this column the
// per-carrier Success/Failed counts already sit one cell over, so reliability is
// still readable without a second number competing for the eye.
// Neutral placeholder for metric cells when a carrier had no bot activity this
// month (totalAttempts === 0): a real value (0%, 0s, "↓ N") would read as a
// genuine result when nothing actually ran.
function renderNoActivity(): React.ReactNode {
  return (
    <Text className={styles.textMuted}>
      <MinusOutlined />
    </Text>
  )
}

// Band filters for the success-rate columns — options mirror the rateColor
// bands so the dropdown speaks the same language as the cell colors, and using
// rateColor itself as the match key keeps the two from ever drifting. Bands
// partition the range, so antd's default multi-select OR composes cleanly.
const RATE_BAND_FILTERS = [
  { text: <Text className={styles.textSuccess}>● ≥ 90%</Text>, value: GREEN },
  { text: <Text className={styles.textGold}>● 70–89%</Text>, value: GOLD },
  { text: <Text className={styles.textDanger}>● {'< 70%'}</Text>, value: RED },
]

// Zero-activity rows render `—`, not a rate — keep them out of every band so
// a "< 70%" filter doesn't surface rows whose cell shows no number at all.
function matchesRateBand(
  row: CarrierSuccessRateDto,
  rate: number,
  band: React.Key | boolean
): boolean {
  return row.totalBenefits > 0 && rateColor(rate) === band
}

function matchesProcessTimeBucket(
  row: CarrierSuccessRateDto,
  bucket: React.Key | boolean
): boolean {
  if (row.totalBenefits === 0) return false
  const sec = row.medianProcessingTimeSec
  if (bucket === ProcessTimeBucket.UnderOneMin) return sec < 60
  if (bucket === ProcessTimeBucket.OneToTwoMin) return sec >= 60 && sec < 120
  return sec >= 120 // OverTwoMin
}

function renderFirstAttemptRate(row: CarrierSuccessRateDto): React.ReactNode {
  if (row.totalBenefits === 0) return renderNoActivity()
  const succeeded = Math.round(
    (row.firstAttemptRate * (row.completedCount + row.failedCount)) / 100
  )
  return (
    <span>
      <Text strong>{row.firstAttemptRate}%</Text>
      <Text type="secondary" style={{ marginLeft: 4 }}>
        ({succeeded})
      </Text>
    </span>
  )
}

function renderTotalAttemptRate(row: CarrierSuccessRateDto): React.ReactNode {
  if (row.totalBenefits === 0) return renderNoActivity()
  const rescueRate = Math.max(
    0,
    Math.round((row.eventualRate - row.firstAttemptRate) * 10) / 10
  )
  const firstSucceeded = Math.round(
    (row.firstAttemptRate * (row.completedCount + row.failedCount)) / 100
  )
  const rescuedCases = Math.max(0, row.completedCount - firstSucceeded)

  if (rescueRate <= 0) {
    return <Text type="secondary">0%</Text>
  }

  return (
    <span>
      <Text strong>+{rescueRate}%</Text>
      <Text type="secondary" style={{ marginLeft: 4 }}>
        (+{rescuedCases})
      </Text>
    </span>
  )
}

function renderMoMDelta(row: CarrierSuccessRateDto): React.ReactNode {
  // A carrier that ran but failed every case (totalAttempts > 0, completed 0)
  // still shows the real decline; only a zero-activity month is blanked.
  if (row.totalBenefits === 0) return renderNoActivity()
  const paid = row.deltaCompletedVsPrev
  return (
    <Text strong>
      {deltaArrow(paid)} {Math.abs(paid)}
    </Text>
  )
}

// Builds the per-carrier health table columns. The Carrier filter list is
// derived from the rows in view, so it stays in sync with the current month.
export function usePaymentBotCarrierColumns(
  rows: CarrierSuccessRateDto[]
): ColumnsType<CarrierSuccessRateDto> {
  return useMemo<ColumnsType<CarrierSuccessRateDto>>(() => {
    const carrierFilters = Array.from(
      new Set(rows.map((r) => r.carrierName))
    ).map((name) => ({ text: name, value: name }))

    return [
      {
        title: 'Carrier',
        key: 'name',
        render: (_value, row) => <Text strong>{row.carrierName}</Text>,
        filters: carrierFilters,
        filterSearch: true,
        onFilter: (value, record) => record.carrierName === value,
        sorter: (a, b) => a.carrierName.localeCompare(b.carrierName),
      },
      {
        title: 'Type',
        key: 'type',
        render: (_value, row) => <BotTypeTag botType={row.botType} />,
        filters: [
          { text: 'WEB AGENT', value: BotType.WEB_AGENT },
          { text: 'IVR', value: BotType.IVR },
        ],
        onFilter: (value, record) => record.botType === value,
        sorter: (a, b) => (a.botType ?? '').localeCompare(b.botType ?? ''),
      },
      {
        title: 'Total Benefits',
        key: 'totalBenefits',
        render: (_value, row) =>
          row.totalBenefits === 0 ? (
            renderNoActivity()
          ) : (
            <Text strong>{row.totalBenefits.toLocaleString()}</Text>
          ),
        sorter: (a, b) => a.totalBenefits - b.totalBenefits,
      },
      {
        title: 'Success / Failed',
        key: 'successFailed',
        render: (_value, row) =>
          row.totalBenefits === 0 ? (
            renderNoActivity()
          ) : (
            <span className={styles.noWrap}>
              <Text
                strong
                style={{ color: rateColor(row.eventualRate), marginRight: 6 }}
              >
                {row.eventualRate}%
              </Text>
              <Text type="secondary">
                ({row.completedCount} / {row.failedCount})
              </Text>
            </span>
          ),
        sorter: (a, b) => {
          if (a.completedCount !== b.completedCount) {
            return a.completedCount - b.completedCount
          }
          return a.failedCount - b.failedCount
        },
      },
      {
        // First-attempt and overall (eventual) success are separate columns so
        // each gets its own sort and threshold filter. Same month, same set of
        // benefits, same denominator → reading across the two cells still gives
        // the "rescued by retry" gap as a clean pp.
        title: 'First-Attempt Success',
        key: 'firstAttemptRate',
        render: (_value, row) => renderFirstAttemptRate(row),
        filters: RATE_BAND_FILTERS,
        onFilter: (value, record) =>
          matchesRateBand(record, record.firstAttemptRate, value),
        sorter: (a, b) => a.firstAttemptRate - b.firstAttemptRate,
      },
      {
        title: '2nd+ Attempts Success',
        key: 'eventualRate',
        render: (_value, row) => renderTotalAttemptRate(row),
        sorter: (a, b) =>
          a.eventualRate -
          a.firstAttemptRate -
          (b.eventualRate - b.firstAttemptRate),
      },
      {
        title: 'Success vs Prev Month',
        key: 'momPaidDelta',
        render: (_value, row) => renderMoMDelta(row),
        sorter: (a, b) => a.deltaCompletedVsPrev - b.deltaCompletedVsPrev,
      },
      {
        title: 'Avg Processing Time',
        key: 'avgTime',
        render: (_value, row) =>
          row.totalBenefits === 0 ? (
            renderNoActivity()
          ) : (
            <span className={styles.inlineFlexCenter}>
              <ClockCircleOutlined className={styles.textMuted} />
              <Text
                strong
                style={{
                  color: processTimeColor(
                    row.medianProcessingTimeSec,
                    row.botType
                  ),
                }}
              >
                {formatDuration(row.medianProcessingTimeSec)}
              </Text>
            </span>
          ),
        filters: PROCESS_TIME_BUCKET_FILTERS,
        onFilter: (value, record) => matchesProcessTimeBucket(record, value),
        sorter: (a, b) => a.medianProcessingTimeSec - b.medianProcessingTimeSec,
      },
    ]
  }, [rows])
}
